import { useEffect, useRef } from "preact/hooks";
import type { StepProps } from "../app";
import { ContactAdvisor } from "../ContactAdvisor";
import { CONSENT_TEXT, CONSENT_VERSION } from "@ks-dac/shared";
import { submitLead } from "../lib/api";
import { track } from "../lib/analytics";
import { PRIVACY_URL } from "../lib/env";

const GENERIC_ERROR = "We could not complete that. Please try again.";
const EXPIRED_TOKEN_FALLBACK = "That address session expired. Please re-enter it.";

const ERROR_ID = "dac-contact-error";
const BLOCKED_ID = "dac-contact-blocked";

/**
 * Firebase callables reject with a `FunctionsError`-shaped object carrying a
 * `code` like `functions/failed-precondition`. Design section 7: an
 * invalid/tampered/expired lead token surfaces as exactly that code, and the
 * client's correct response is to send the customer back to step 1, not to
 * repeat the generic error forever against a token that can never succeed.
 */
function isExpiredTokenError(err: unknown): err is { code?: unknown; message?: unknown } {
  return (
    typeof err === "object" &&
    err !== null &&
    "code" in err &&
    (err as { code?: unknown }).code === "functions/failed-precondition"
  );
}

/** Step 2: contact details and consent. Submitting is what creates the lead. */
export function Step2Contact({ state, update }: StepProps) {
  // A plain ref, not `state.busy`: a second click landing before the re-render
  // that disables this button commits still runs against the render-N
  // closure, where `state.busy` reads stale (false). A ref mutates
  // synchronously, so it is already true by the time a second click in the
  // same tick re-enters this function, and the guard below actually holds.
  const submitInFlight = useRef(false);

  // Fires once per entry to this step (this component only mounts while
  // state.step === "contact", including a re-entry after Step1 sends the
  // customer back for an expired token).
  useEffect(() => {
    track("step_view", { step: "contact", submission_id: state.submissionId });
  }, []);

  /**
   * What is still outstanding, in field order.
   *
   * A submit button that is disabled with no stated reason is a dead end: it is
   * not in the tab order in some browsers, carries no hint, and gives a customer
   * who cannot see which box is empty nothing to act on. Rendering the list and
   * pointing the button's aria-describedby at it turns "why can't I continue"
   * into an answer. The disabled state itself is kept, because it is what the
   * existing tests pin and it is the right affordance once the reason is legible.
   */
  const missing: string[] = [];
  if (state.firstName.trim() === "") missing.push("your first name");
  if (state.lastName.trim() === "") missing.push("your last name");
  if (!state.email.includes("@")) missing.push("an email address");
  if (!state.contactConsent) missing.push("the box above ticked");

  const ready = missing.length === 0 && state.token !== null;

  const onSubmit = async () => {
    if (!state.token) return;
    if (submitInFlight.current) return;
    submitInFlight.current = true;
    update({ busy: true, error: null });

    try {
      const verdict = await submitLead({
        token: state.token,
        submissionId: state.submissionId,
        contact: {
          firstName: state.firstName.trim(),
          lastName: state.lastName.trim(),
          email: state.email.trim(),
          phone: state.phone.trim() === "" ? null : state.phone.trim(),
        },
        consent: {
          contactConsent: true,
          contactConsentAt: new Date().toISOString(),
          // What THIS build rendered. The server records it rather than checking
          // it, because the widget and the functions deploy on different cadences.
          consentVersion: CONSENT_VERSION,
        },
        source: {
          site: document.referrer || "direct",
          page: window.location.pathname,
          embed: new URLSearchParams(window.location.search).has("embed") ? "embed" : "standalone",
        },
        enteredAddress: state.address,
      });

      // The lead moment. GA4's recommended event name, so it works as a Google
      // Ads conversion without further mapping. No PII in the params.
      track("generate_lead", { submission_id: state.submissionId });
      track("step_complete", { step: "contact", submission_id: state.submissionId });
      update({ busy: false, verdict, step: "result" });
    } catch (err) {
      if (isExpiredTokenError(err)) {
        // Back to step 1, not a dismissal of this screen: the token that just
        // failed is dead for good, and the customer's contact fields are left
        // untouched in state so only the address needs re-entering.
        const message =
          typeof err.message === "string" && err.message.trim() !== ""
            ? err.message
            : EXPIRED_TOKEN_FALLBACK;
        update({ busy: false, step: "address", token: null, error: message });
      } else {
        update({ busy: false, error: GENERIC_ERROR });
      }
    } finally {
      submitInFlight.current = false;
    }
  };

  return (
    <section class="step" aria-busy={state.busy}>
      <h2>How can we contact you?</h2>

      {/* A real <form> so Enter submits from any field, which is the reflex on a
          contact screen. `noValidate` keeps the messaging in the page (the
          blocked-reason line below) rather than in an unstyleable browser
          bubble that would also fire before our own guard runs. */}
      <form
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (state.busy || !ready) return;
          void onSubmit();
        }}
      >
        <div class="grid">
          <label class="field" for="dac-first-name">
            First name <span class="req" aria-hidden="true">*</span>
            <input
              id="dac-first-name"
              required
              autocomplete="given-name"
              value={state.firstName}
              onInput={(e) => update({ firstName: e.currentTarget.value })}
            />
          </label>
          <label class="field" for="dac-last-name">
            Last name <span class="req" aria-hidden="true">*</span>
            <input
              id="dac-last-name"
              required
              autocomplete="family-name"
              value={state.lastName}
              onInput={(e) => update({ lastName: e.currentTarget.value })}
            />
          </label>
          <label class="field span2" for="dac-email">
            Email <span class="req" aria-hidden="true">*</span>
            <input
              id="dac-email"
              type="email"
              required
              inputMode="email"
              autocomplete="email"
              value={state.email}
              onInput={(e) => update({ email: e.currentTarget.value })}
            />
          </label>
          <label class="field span2" for="dac-phone">
            Phone <span class="optional">(optional)</span>
            <input
              id="dac-phone"
              type="tel"
              inputMode="tel"
              autocomplete="tel"
              value={state.phone}
              onInput={(e) => update({ phone: e.currentTarget.value })}
            />
          </label>
        </div>

        <label class="consent">
          <input
            type="checkbox"
            required
            checked={state.contactConsent}
            onChange={(e) => update({ contactConsent: e.currentTarget.checked })}
          />
          {/* Rendered from the pinned constant, never retyped here. Editing the
              sentence in place would break the hash pin in shared/src/lead.ts,
              which is exactly what that pin is for. */}
          <span>{CONSENT_TEXT}</span>
        </label>

        {/* The policy link and nothing else. The sentence that used to lead this
            line described sending the result by email, which nothing does: the
            widget renders the verdict on the next screen. The consent box above
            is what states the actual use of these details, and it is pinned. */}
        <p class="hint">
          See our{" "}
          <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer">
            Privacy Policy
          </a>{" "}
          (opens in a new tab).
        </p>

        {state.error && (
          <p class="notice" id={ERROR_ID} role="alert">
            {state.error}
          </p>
        )}

        <div class="actions">
          <button
            type="button"
            disabled={state.busy}
            onClick={() => update({ step: "address", error: null })}
          >
            Back
          </button>
          {/* Centred between Back and the primary by .actions' space-between. */}
          <ContactAdvisor stepName="Contact" submissionId={state.submissionId} />
          <button
            type="submit"
            class="primary"
            disabled={state.busy || !ready}
            aria-describedby={
              [missing.length > 0 ? BLOCKED_ID : null, state.error ? ERROR_ID : null]
                .filter(Boolean)
                .join(" ") || undefined
            }
          >
            {state.busy ? "Checking…" : "See my result"}
          </button>
        </div>

        {/* On `missing`, not on `!ready`: `ready` is also false when the lead
            token is absent, and that is not something the customer can act on
            from this screen (it sends them back to step 1 instead), so it must
            not render as an empty "Still needed: ." line. */}
        {missing.length > 0 && (
          <p class="blocked-reason" id={BLOCKED_ID}>
            Still needed: {missing.join(", ")}.
          </p>
        )}
      </form>

      <p class="sr-only" role="status">
        {state.busy ? "Checking the address." : ""}
      </p>
    </section>
  );
}
